import { PER_PAGE_OPTIONS } from '@/constants/pagination'

const state = {
  specialPricingProducts: [],
  specialPricingCustomers: [],

  loadingSpecialPricingProd: '',
  loadingSpecialPricingCust: '',

  // products pagination
  totalSpecialPricingProducts: 0,
  perPageSpecialPricingProducts: PER_PAGE_OPTIONS[0],
  pageNrSpecialPricingProducts: 0,

  // customers pagination
  totalSpecialPricingCustomers: 0,
  perPageSpecialPricingCustomers: PER_PAGE_OPTIONS[0],
  pageNrSpecialPricingCustomers: 0,

  // products sorting
  sortBySpecialPricingProducts: 'code',
  ascSpecialPricingProducts: true,

  // customers sorting
  sortBySpecialPricingCustomers: 'companies.name',
  ascSpecialPricingCustomers: true,

  // products filters
  filtersSpecialPricingProducts: {
    product: { type: 'text', label: 'Product Name', value: '' },
    code: { type: 'text', label: 'Code', value: '' },
    type: { type: 'text', label: 'Product Type', value: '' },
    specific: { type: 'text', label: 'Special Pricing', value: 'true' },
    prices: { type: 'text', label: 'Active Prices', value: 'active' }
  },

  // customers filters
  filtersSpecialPricingCustomers: {
    company: { type: 'text', label: 'Customer Name', value: '' },
    code: { type: 'text', label: 'Customer Code', value: '' },
    specific: { type: 'text', label: 'Special Pricing', value: 'true' },
    prices: { type: 'text', label: 'Active Prices', value: 'active' }
  }
}

const getters = {
  specialPricingProducts: (state) => state.specialPricingProducts,
  specialPricingCustomers: (state) => state.specialPricingCustomers,

  loadingSpecialPricingProd: (state) => state.loadingSpecialPricingProd,
  loadingSpecialPricingCust: (state) => state.loadingSpecialPricingCust,

  totalSpecialPricingProducts: (state) => state.totalSpecialPricingProducts,
  perPageSpecialPricingProducts: (state) => state.perPageSpecialPricingProducts,
  pageNrSpecialPricingProducts: (state) => state.pageNrSpecialPricingProducts,

  totalSpecialPricingCustomers: (state) => state.totalSpecialPricingCustomers,
  perPageSpecialPricingCustomers: (state) =>
    state.perPageSpecialPricingCustomers,
  pageNrSpecialPricingCustomers: (state) => state.pageNrSpecialPricingCustomers,

  sortBySpecialPricingProducts: (state) => state.sortBySpecialPricingProducts,
  ascSpecialPricingProducts: (state) => state.ascSpecialPricingProducts,

  sortBySpecialPricingCustomers: (state) => state.sortBySpecialPricingCustomers,
  ascSpecialPricingCustomers: (state) => state.ascSpecialPricingCustomers,

  filtersSpecialPricingProducts: (state) => state.filtersSpecialPricingProducts,
  filtersSpecialPricingCustomers: (state) =>
    state.filtersSpecialPricingCustomers,

  offsetSpecialPricingProducts: (state) => {
    return (
      state.perPageSpecialPricingProducts * state.pageNrSpecialPricingProducts
    )
  },

  offsetSpecialPricingCustomers: (state) => {
    return (
      state.perPageSpecialPricingCustomers * state.pageNrSpecialPricingCustomers
    )
  }
}

const actions = {
  async fetchSpecialPricingProducts({ commit, dispatch, getters }) {
    commit('SET_LOADING_SPECIAL_PRICING_PROD', 'list')

    const res = await dispatch(
      'fetch',
      {
        endpoint: 'productlist/',
        parameters: {
          limit: getters.perPageSpecialPricingProducts,
          offset: getters.offsetSpecialPricingProducts,
          sort_by: getters.sortBySpecialPricingProducts,
          ascending: getters.ascSpecialPricingProducts
        },
        filters: getters.filtersSpecialPricingProducts
      },
      { root: true }
    )

    commit('SET_LOADING_SPECIAL_PRICING_PROD', '')

    if (!res.error) {
      commit('SET_SPECIAL_PRICING_PRODUCTS', res.data)
      commit('SET_TOTAL_SPECIAL_PRICING_PRODUCTS', res.total)
    } else {
      const messagePrefix = 'Error while loading special pricing products! '
      const { error } = res
      dispatch('showError', { error, messagePrefix })
    }
  },

  async fetchSpecialPricingCustomers({ commit, dispatch, getters }) {
    commit('SET_LOADING_SPECIAL_PRICING_CUST', 'list')

    const res = await dispatch(
      'fetch',
      {
        endpoint: 'customers/',
        parameters: {
          limit: getters.perPageSpecialPricingCustomers,
          offset: getters.offsetSpecialPricingCustomers,
          sort_by: getters.sortBySpecialPricingCustomers,
          ascending: getters.ascSpecialPricingCustomers
        },
        filters: getters.filtersSpecialPricingCustomers
      },
      { root: true }
    )

    commit('SET_LOADING_SPECIAL_PRICING_CUST', '')

    if (!res.error) {
      commit('SET_SPECIAL_PRICING_CUSTOMERS', res.data)
      commit('SET_TOTAL_SPECIAL_PRICING_CUSTOMERS', res.total)
    } else {
      const messagePrefix = 'Error while loading special pricing customers! '
      const { error } = res
      dispatch('showError', { error, messagePrefix })
    }
  },

  async fetchSpecialPricingPageData({ dispatch }) {
    await Promise.all([
      dispatch('fetchSpecialPricingProducts'),
      dispatch('fetchSpecialPricingCustomers')
    ])
  },

  clearSpecialPricingProductFilters({ commit }) {
    commit('SET_FILTER_SPECIAL_PRICING_PRODUCT', {
      field: 'product',
      value: ''
    })
    commit('SET_FILTER_SPECIAL_PRICING_PRODUCT', {
      field: 'code',
      value: ''
    })
    commit('SET_FILTER_SPECIAL_PRICING_PRODUCT', {
      field: 'type',
      value: ''
    })
  },

  clearSpecialPricingCustomerFilters({ commit }) {
    commit('SET_FILTER_SPECIAL_PRICING_CUSTOMER', {
      field: 'company',
      value: ''
    })
    commit('SET_FILTER_SPECIAL_PRICING_CUSTOMER', {
      field: 'code',
      value: ''
    })
  },

  clearSpecialPricingState({ commit }) {
    commit('SET_SPECIAL_PRICING_PRODUCTS', [])
    commit('SET_SPECIAL_PRICING_CUSTOMERS', [])
    commit('SET_TOTAL_SPECIAL_PRICING_PRODUCTS', 0)
    commit('SET_TOTAL_SPECIAL_PRICING_CUSTOMERS', 0)
  }
}

const mutations = {
  SET_SPECIAL_PRICING_PRODUCTS: (state, payload) => {
    state.specialPricingProducts = payload
  },

  SET_SPECIAL_PRICING_CUSTOMERS: (state, payload) => {
    state.specialPricingCustomers = payload
  },

  SET_LOADING_SPECIAL_PRICING_PROD: (state, payload) => {
    state.loadingSpecialPricingProd = payload
  },
  SET_LOADING_SPECIAL_PRICING_CUST: (state, payload) => {
    state.loadingSpecialPricingCust = payload
  },

  SET_TOTAL_SPECIAL_PRICING_PRODUCTS: (state, payload) => {
    state.totalSpecialPricingProducts = payload
  },

  SET_PER_PAGE_SPECIAL_PRICING_PRODUCTS: (state, payload) => {
    state.perPageSpecialPricingProducts = payload
  },

  SET_PAGE_NR_SPECIAL_PRICING_PRODUCTS: (state, payload) => {
    state.pageNrSpecialPricingProducts = payload
  },

  SET_SORT_BY_SPECIAL_PRICING_PRODUCTS: (state, payload) => {
    state.sortBySpecialPricingProducts = payload
  },

  SET_ASC_SPECIAL_PRICING_PRODUCTS: (state, payload) => {
    state.ascSpecialPricingProducts = payload
  },

  SET_TOTAL_SPECIAL_PRICING_CUSTOMERS: (state, payload) => {
    state.totalSpecialPricingCustomers = payload
  },

  SET_PER_PAGE_SPECIAL_PRICING_CUSTOMERS: (state, payload) => {
    state.perPageSpecialPricingCustomers = payload
  },

  SET_PAGE_NR_SPECIAL_PRICING_CUSTOMERS: (state, payload) => {
    state.pageNrSpecialPricingCustomers = payload
  },

  SET_SORT_BY_SPECIAL_PRICING_CUSTOMERS: (state, payload) => {
    state.sortBySpecialPricingCustomers = payload
  },

  SET_ASC_SPECIAL_PRICING_CUSTOMERS: (state, payload) => {
    state.ascSpecialPricingCustomers = payload
  },

  SET_FILTER_SPECIAL_PRICING_PRODUCT: (state, payload) => {
    state.filtersSpecialPricingProducts[payload.field].value = payload.value
  },

  SET_FILTER_SPECIAL_PRICING_CUSTOMER: (state, payload) => {
    state.filtersSpecialPricingCustomers[payload.field].value = payload.value
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
